const AUDIO_FEATURE_BUCKETS = 16;

const emptyFeatures = () => new Array(AUDIO_FEATURE_BUCKETS + 2).fill(0);

const samplesToAudioFeatures = samples => {
    const source = samples || new Float32Array(0);
    if (!source.length) {
        return emptyFeatures();
    }

    const bucketSize = Math.max(1, Math.ceil(source.length / AUDIO_FEATURE_BUCKETS));
    const features = [];
    let totalAbs = 0;
    let zeroCrossings = 0;

    for (let i = 0; i < AUDIO_FEATURE_BUCKETS; i++) {
        const start = i * bucketSize;
        const end = Math.min(source.length, start + bucketSize);
        let sumSquares = 0;

        for (let j = start; j < end; j++) {
            const sample = source[j] || 0;
            sumSquares += sample * sample;
            totalAbs += Math.abs(sample);
            if (j > 0 && ((source[j - 1] < 0 && sample >= 0) || (source[j - 1] >= 0 && sample < 0))) {
                zeroCrossings++;
            }
        }

        features.push(end > start ? Math.sqrt(sumSquares / (end - start)) : 0);
    }

    features.push(totalAbs / source.length);
    features.push(zeroCrossings / source.length);
    return features;
};

const createAudioClassifier = () => ({
    examples: [],
    addExample (label, features) {
        this.examples.push({
            label,
            features: Array.from(features || [])
        });
    },
    clearAllClasses () {
        this.examples = [];
    }
});

const getDistance = (left, right) => {
    const length = Math.max(left.length, right.length);
    let sumSquares = 0;
    for (let i = 0; i < length; i++) {
        const delta = (left[i] || 0) - (right[i] || 0);
        sumSquares += delta * delta;
    }
    return Math.sqrt(sumSquares);
};

const classifyAudioFeatures = (classifier, features) => {
    const examples = classifier && classifier.examples ? classifier.examples : [];
    if (!examples.length) {
        return {
            label: '',
            confidences: {}
        };
    }

    const distances = examples.map(example => ({
        label: example.label,
        distance: getDistance(features || [], example.features)
    }));
    const nearest = distances.reduce((best, result) => (
        result.distance < best.distance ? result : best
    ), distances[0]);

    const similaritiesByLabel = distances.reduce((result, item) => {
        result[item.label] = (result[item.label] || 0) + (1 / (1 + item.distance));
        return result;
    }, {});
    const totalSimilarity = Object.keys(similaritiesByLabel).reduce((sum, label) => (
        sum + similaritiesByLabel[label]
    ), 0);
    const confidences = Object.keys(similaritiesByLabel).reduce((result, label) => {
        result[label] = totalSimilarity ? similaritiesByLabel[label] / totalSimilarity : 0;
        return result;
    }, {});

    return {
        label: nearest.label,
        confidences
    };
};

export {
    classifyAudioFeatures,
    createAudioClassifier,
    samplesToAudioFeatures
};
